import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Button,
  Container,
  Divider,
  Grid,
  Icon,
  Modal,
  Segment,
  Image,
  Dimmer,
  Header
} from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { getAssetUrl, getFileType } from './Utils/asset.utils';
import {
  makeDelete,
  makeGetWithParam,
  makePut
} from './../../../services/ServiceManager';

import domains from '../../../containers/Domains';
import modes from './constants/modes';
import errors from './constants/errors';

// eslint-disable-next-line
import worker from 'worker-loader!./Workers/fb.worker.js';
import * as AppActions from './../../../actions';
import AssetGalleryNav from './AssetGalleryNav';
import AssetList from './AssetList';
import FilterPanel from './FilterPanel';
import UploaderPanel from './UploaderPanel';

class AssetGallery extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      openModal: false,
      openFilterPanel: false,
      openUploaderPanel: false,
      mode: modes.ASSET_GALLERY.NORMAL, // NORMAL, UPLOAD_PENDING (uploaded panel opened), UPLOADING, SEARCHING, EDITTING
      error: {
        type: null, // NO_ASSET, NOT_ENOUGH_TAGS, UPLOADING_INTERRUPTED, INVALID_USER, RUNNING_WORKER, SELECTED_ASSET_FAIL or null
        title: '',
        message: ''
      },

      /**
       * This tagOptions will be used as dropdown options in AssetTagSelection.
       * The data will be filled as user interacts with the dropdowns in AssetTagSelection for each category
       * and the format will be the following:
       *  {
       *    key: tag.id,
       *    text: tag.value,
       *    value: tag.id,
       *    category: tag.category
       * }
       */
      tagOptions: {},
      uploadedAssets: [],
      selectedAssets: [],
      selectedAssetToDisplay: null,
      selectedAssetUrl: null,
      selectedAssetDataLoading: false,
      showSelectedAssetMenu: false,
      updatedAssetTags: {
        assetIds: [],
        tags: []
      },
      deletedAssets: [],
      searchParams: { archived: 'false' },
      processedFiles: [],
      fbWorker: null, // this will be instanciated when user starts uploading...
      shouldClearSearch: false,
      refreshAssetList: false, // this will be used when user deleted assets
      addedTagCategory: '',
      inArchiveMode: false
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.closeGalleryModal, false);
    const { saveGTMEvent, setSectionTitle } = this.props;

    if (saveGTMEvent) saveGTMEvent('Asset Gallery', 'Asset Gallery');
    if (setSectionTitle) setSectionTitle('Asset Gallery');
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.closeGalleryModal, false);
  }

  closeGalleryModal = ({ keyCode }) => {
    if (keyCode === 27) {
      const { openModal } = this.state;
      if (openModal) {
        this.setState((prevState) => ({
          ...prevState,
          openModal: false,
          inArchiveMode: false,
          searchParams: {}
        }));
      }
    }
  };

  onModalOpen = () => {
    this.setState((prevState) => ({
      ...prevState,
      openModal: true
    }));
  };

  onModalClose = () => {
    // If any processes are running in background (fb.worker.js),
    // let the user know that he/she has to wait untill the processes are complete.

    const { processedFiles } = this.state;

    const numberOfFilesInWorker = processedFiles.filter(
      (file) => file.workerInProgress
    ).length;

    if (numberOfFilesInWorker > 0) {
      this.setState((prevState) => ({
        ...prevState,
        error: {
          type: errors.ASSET_GALLERY.RUNNING_WORKER,
          title: 'Background processes are running',
          message:
            'Files are being uploaded in the background. Please wait until the processes are complete.'
        }
      }));
      return;
    }

    this.setState((prevState) => ({
      ...prevState,
      inArchiveMode: false,
      openModal: false,
      searchParams: {}
    }));
  };

  onErrorModalClose = () => {
    this.setState((prevState) => ({
      ...prevState,
      error: {
        type: null,
        title: '',
        message: ''
      }
    }));
  };

  /**
   * This function will be called when new tag has been added.
   * This will sets addedNewTag to true in state so that other parts of componenet can refresh the tag options.
   */
  onTagAdd = (category) => {
    const { tagOptions } = this.state;

    const tagOptionsCopy = {
      ...tagOptions
    };

    delete tagOptionsCopy[category]; // this part is needed to reload the tag options when the dropdown is clicked

    this.setState(
      {
        ...this.state,
        addedTagCategory: category,
        tagOptions: tagOptionsCopy
      },
      () => {
        this.setState((prevState) => ({
          ...prevState,
          addedTagCategory: ''
        }));
      }
    );
  };

  onAssetSelectButtonClick = (event) => {
    event.preventDefault();

    const { selectedAssets } = this.state;

    // Can select only one asset
    // If selectedAssets contains more than one. we need to check the selection logic before this function call.

    if (!selectedAssets || selectedAssets.length !== 1)
      throw new Error(
        'More than one assets found. Only one asset can be selected.'
      );

    const selectedAsset = selectedAssets[0];

    this.setState(
      {
        ...this.state,
        selectedAssetDataLoading: true,
        searchParams: {}
      },
      this.loadSelectedAssetData(selectedAsset)
    );
  };

  /**
   * This function returns callback that will be used in this.setState().
   * It is a wrapper function that takes the selected asset.
   * Seel this.onAssetSelectButtonClick() for more detail.
   */
  // eslint-disable-next-line
  loadSelectedAssetData = (asset) => {
    return () => {
      const { id: assetId } = asset;

      makeGetWithParam('service/tags/asset', null, assetId).then((response) => {
        const { statusCode, assetTags } = response || {};

        if (!statusCode || statusCode !== 200) {
          this.setState((prevState) => ({
            ...prevState,
            error: {
              type: errors.ASSET_GALLERY.SELECTED_ASSET_FAIL,
              title: 'Unable to select the asset',
              message:
                'There was an error while retreving neccessary data for the selected asset. Please try again or ask for help.'
            },
            selectedAssetDataLoading: false
          }));

          return;
        }
        console.log('response', response);
        console.log('aset', asset);
        // reformat the selected asset same as the one that the old asset gallery returns so that it works with other modules.
        const reformatedAsset = {
          ...asset,
          created_at: asset.created_at ? asset.created_at.toString() : '-',
          uploaded_asset_id: assetId,
          account_id: domains.assetLibraryAccountId,
          assetTags
        };

        this.setState(
          {
            ...this.state,
            selectedAssetToDisplay: asset,
            selectedAssetUrl: getAssetUrl(asset),
            openModal: false,
            selectedAssetDataLoading: false
          },
          () => {
            // re-format the asset so that it matches with the selected asset from the old asset gallery.

            const { onAssetSelected } = this.props;

            if (onAssetSelected) onAssetSelected(reformatedAsset);
          }
        );
      });
    };
  };

  onSelectedAssetHover = () => {
    this.setState((prevState) => ({
      ...prevState,
      showSelectedAssetMenu: true
    }));
  };

  onSelectedAssetLeave = () => {
    this.setState((prevState) => ({
      ...prevState,
      showSelectedAssetMenu: false
    }));
  };

  onAssetRemoveButtonClick = (event) => {
    event.preventDefault();

    this.setState((prevState) => ({
      ...prevState,
      selectedAssetToDisplay: null,
      selectedAssetUrl: null,
      showSelectedAssetMenu: false
    }));
  };

  clearSearch = () => {
    this.setState(
      {
        ...this.state,
        searchParams: {},
        shouldClearSearch: true
      },
      () => {
        this.setState((prevState) => ({
          ...prevState,
          shouldClearSearch: false
        }));
      }
    );
  };

  displayError = (error) => {
    if (!error || !error.type) {
      throw new Error('displayError() was called with invalid error object.');
    }

    this.setState((prevState) => ({
      ...prevState,
      error
    }));
  };

  deleteSelectedAssets = (callback) => {
    const { selectedAssets } = this.state;

    const selectedAssetsIds = selectedAssets.map((asset) => asset.id);

    Promise.all(
      selectedAssetsIds.map((id) =>
        makeDelete('service/asset/asset', { id }, null)
      )
    ).then((results) => {
      const didFail =
        results.filter((result) => result.statusCode !== 200).length > 0;
      if (didFail) {
        callback({
          type: 'DELETE_ASSET_FAIL',
          message: 'Deleting selected assets failed. Please try again.'
        });

        return;
      }

      this.setState(
        {
          ...this.state,
          deletedAssets: selectedAssets,
          refreshAssetList: true
        },
        () => {
          this.setState((prevState) => ({
            ...prevState,
            deletedAssets: [],
            refreshAssetList: false
          }));

          callback();
        }
      );
    });
  };

  searchAssets = (key, value, callback = null) => {
    this.setState(
      {
        ...this.state,
        searchParams: {
          ...this.state.searchParams,
          [key]: value
        }
      },
      callback
    );
  };

  /**
   * @param {bool} uploading It indicates if the upload process is being running.
   * @param {object[]} processedFiles
   * @param {func} callback This callback function is meant to rund after the state changes.
   *
   * NOTE
   *
   * The processedFiles will be used for updating the warning status that is responsible for displaying how many files are being processed in background.
   */
  updateUploaderStatus = (uploading, processedFiles = [], callback = null) => {
    const { fbWorker: existingWorker } = this.state;

    let fbWorker = null;
    if (!existingWorker && uploading) {
      fbWorker = worker();

      fbWorker.addEventListener('message', ({ data }) => {
        const { assetId } = data;

        const { processedFiles: existingFiles } = this.state;

        const newProcessedFiles = existingFiles.map((file) => {
          const { asset } = file || {};
          const { id: existingAssetId } = asset || {};

          if (existingAssetId === assetId) {
            return {
              ...file,
              workerInProgress: false
            };
          }

          return file;
        });

        this.setState((prevState) => ({
          ...prevState,
          processedFiles: newProcessedFiles
        }));
      });
    }

    this.setState(
      {
        ...this.state,
        mode: uploading
          ? modes.ASSET_GALLERY.UPLOADING
          : modes.ASSET_GALLERY.UPLOAD_PENDING,
        processedFiles,
        fbWorker
      },
      callback
    );
  };

  updateGalleryMode = (mode) => {
    this.setState((prevState) => ({
      ...prevState,
      mode,
      selectedAssets: []
    }));
  };

  updateTagOptions = (tagOption, callback) => {
    const keys = Object.keys(tagOption);

    if (keys.length !== 1) {
      throw new Error(
        'The parameter has more or less keys than expected. Please check where it was called.'
      );
    }

    this.setState(
      {
        ...this.state,
        tagOptions: {
          ...this.state.tagOptions,
          ...tagOption
        }
      },
      () => {
        if (callback) callback();
      }
    );
  };

  /**
   * NOTE: This function has been added to populate the options for selected assets.
   * The options are needed to pre-fill the tags under selected asset.
   * Sending request to get the options will be done in the component that calls this function.
   * Just update the state.
   */
  updateMultipleTagOptions = (tagOptions, callback = null) => {
    this.setState(
      {
        ...this.state,
        tagOptions: {
          ...this.state.tagOptions,
          ...tagOptions
        }
      },
      () => {
        if (callback) callback();
      }
    );
  };

  updateAssetList = (uploadedAssets) => {
    this.setState(
      {
        ...this.state,
        uploadedAssets
      },
      () => {
        this.setState((prevState) => ({
          ...prevState,
          uploadedAssets: []
        }));
      }
    );
  };

  updateSelectedAssets = (assets) => {
    this.setState((prevState) => ({
      ...prevState,
      selectedAssets: assets
    }));
  };

  updateAssetTags = (assetIds, tags) => {
    const { selectedAssets } = this.state;

    let newSelectedAssets = [...selectedAssets];

    // update the tags for selectedAssets
    if (selectedAssets && selectedAssets.length > 0) {
      newSelectedAssets = selectedAssets.map((asset) => {
        const { id } = asset;

        if (assetIds.indexOf(id) !== -1) {
          return {
            ...asset,
            tags
          };
        }

        return {
          ...asset
        };
      });
    }

    this.setState(
      {
        ...this.state,
        updatedAssetTags: {
          assetIds,
          tags
        },
        selectedAssets: newSelectedAssets
      },
      () => {
        this.setState((prevState) => ({
          ...prevState,
          updatedAssetTags: {
            assetIds: [],
            tags: []
          }
        }));
      }
    );
  };

  toggleFilterPanel = (open) => {
    // Handle case when user tries to close the panel while assets are being uploaded.
    const { mode, openUploaderPanel } = this.state;

    if (mode === modes.ASSET_GALLERY.UPLOADING && openUploaderPanel) {
      this.setState((prevState) => ({
        ...prevState,
        error: {
          type: errors.ASSET_GALLERY.UPLOADING_INTERRUPTED,
          title: 'Uploading assets in progress',
          message:
            'Assets are currently being uploaded. Please wait until the processes are complete. It usually takes less than 1 min per file.'
        }
      }));

      return;
    }

    this.setState((prevState) => ({
      ...prevState,
      openFilterPanel: open,
      mode: open ? modes.ASSET_GALLERY.SEARCHING : modes.ASSET_GALLERY.NORMAL,
      openUploaderPanel: false // close uploader panel if it was open.
    }));
  };

  toggleUploaderPanel = (open) => {
    // Handle case when user tries to close the panel while assets are being uploaded.
    const { mode } = this.state;

    if (mode === modes.ASSET_GALLERY.UPLOADING && !open) {
      this.setState((prevState) => ({
        ...prevState,
        error: {
          type: errors.ASSET_GALLERY.UPLOADING_INTERRUPTED,
          title: 'Uploading assets in progress',
          message:
            'Assets are currently being uploaded. Please wait until the processes are complete. It usually takes less than 1 min per file.'
        }
      }));

      return;
    }

    this.setState((prevState) => ({
      ...prevState,
      openUploaderPanel: open,
      mode: open
        ? modes.ASSET_GALLERY.UPLOAD_PENDING
        : modes.ASSET_GALLERY.NORMAL,
      openFilterPanel: false // close filter panel if it was open.
    }));
  };

  toggleArchiveMode = () => {
    this.setState(
      (prevState) => ({ inArchiveMode: !prevState.inArchiveMode }),
      () => {
        const { searchParams, inArchiveMode } = this.state;
        this.setState({
          searchParams: {
            ...searchParams,
            archived: `${inArchiveMode}`
          }
        });
      }
    );
  };

  toggleArchivedStatusForAsset = async (
    assetId,
    assetFilename,
    toBeArchivedBool,
    callback = null
  ) => {
    const data = { assetId, toBeArchivedBool };
    let toastMessage = '';
    const result = await makePut('/service/asset/archive', data, null);
    const { statusCode } = result;
    if (statusCode !== 200) {
      toastMessage = toBeArchivedBool
        ? `Something went wrong with archiving  ${assetFilename}`
        : `Something went wrong with unarchiving  ${assetFilename}`;
      toast.warn(toastMessage, {
        position: 'top-center',
        autoClose: 3500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false
      });
      return;
    }
    toastMessage = toBeArchivedBool
      ? `The asset  ${assetFilename} has been archived.`
      : `The asset  ${assetFilename} has been unarchived.`;
    toast.info(toastMessage, {
      position: 'top-center',
      autoClose: 3500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false
    });

    if (callback) callback();
  };

  render() {
    const {
      openModal,
      openFilterPanel,
      openUploaderPanel,
      error,
      mode,
      tagOptions,
      uploadedAssets,
      selectedAssets,
      selectedAssetToDisplay,
      updatedAssetTags,
      deletedAssets,
      searchParams,
      fbWorker,
      processedFiles,
      shouldClearSearch,
      refreshAssetList,
      addedTagCategory,
      selectedAssetUrl,
      showSelectedAssetMenu,
      selectedAssetDataLoading,
      inArchiveMode
    } = this.state;
    const { getFbToken, user, displayPreview } = this.props;

    const selectedAssetFileType = selectedAssetToDisplay
      ? getFileType(selectedAssetToDisplay)
      : '';

    return (
      <div>
        <Segment
          style={
            displayPreview
              ? { width: '25em', position: 'relative' }
              : { position: 'relative' }
          }
        >
          <Grid columns={2} divided>
            <Grid.Row>
              <Grid.Column width={4} verticalAlign="middle">
                <Button onClick={this.onModalOpen} icon>
                  <Icon name="upload" />
                </Button>
              </Grid.Column>

              <Grid.Column width={8} verticalAlign="middle">
                {!selectedAssetToDisplay && displayPreview && (
                  <span>No asset selected</span>
                )}

                {selectedAssetToDisplay && displayPreview && (
                  <Container style={{ position: 'relative' }}>
                    <Dimmer.Dimmable
                      dimmed={showSelectedAssetMenu}
                      onMouseEnter={this.onSelectedAssetHover}
                      onMouseLeave={this.onSelectedAssetLeave}
                    >
                      {selectedAssetFileType.indexOf('video') !== -1 && (
                        <video
                          src={selectedAssetUrl}
                          type="video/mp4"
                          muted
                          autoPlay
                          style={{ width: '10em', height: '10em' }}
                        >
                          <track kind="captions" />
                        </video>
                      )}
                      {selectedAssetFileType.indexOf('video') === -1 && (
                        <Image
                          src={selectedAssetUrl}
                          style={{ width: '10em', height: '10em' }}
                        />
                      )}
                      <Dimmer active={showSelectedAssetMenu}>
                        <Header as="h5" inverted>
                          Do you want to remove?
                        </Header>
                        <Button
                          negative
                          onClick={this.onAssetRemoveButtonClick}
                        >
                          Remove
                        </Button>
                      </Dimmer>
                    </Dimmer.Dimmable>
                  </Container>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        <Modal
          size="fullscreen"
          open={openModal}
          style={{
            minWidth: '1370px'
          }}
        >
          {inArchiveMode ? (
            <Modal.Header
              style={{ backgroundColor: '#ed9797', color: '#474747' }}
            >
              Asset Gallery (Archive)
            </Modal.Header>
          ) : (
            <Modal.Header>Asset Gallery</Modal.Header>
          )}
          <Modal.Content>
            <AssetGalleryNav
              mode={mode}
              user={user}
              tagOptions={tagOptions}
              selectedAssets={selectedAssets}
              processedFiles={processedFiles}
              shouldClearSearch={shouldClearSearch}
              didAssetSelected={selectedAssets.length > 0}
              isFilterPanelOpened={openFilterPanel}
              isUploaderPanelOpened={openUploaderPanel}
              toggleFilterPanel={this.toggleFilterPanel}
              toggleUploaderPanel={this.toggleUploaderPanel}
              updateGalleryMode={this.updateGalleryMode}
              updateSelectedAssets={this.updateSelectedAssets}
              updateAssetTags={this.updateAssetTags}
              deleteSelectedAssets={this.deleteSelectedAssets}
              searchAssets={this.searchAssets}
              updateTagOptions={this.updateTagOptions}
              clearSearch={this.clearSearch}
            />
            <Divider />
            <Container fluid style={{ position: 'relative' }}>
              <FilterPanel
                user={user}
                shouldClearSearch={shouldClearSearch}
                addedTagCategory={addedTagCategory}
                openFilterPanel={openFilterPanel}
                toggleFilterPanel={this.toggleFilterPanel}
                searchAssets={this.searchAssets}
                displayError={this.displayError}
                toggleArchiveMode={this.toggleArchiveMode}
                inArchiveMode={inArchiveMode}
              />
              <UploaderPanel
                user={user}
                getFbToken={getFbToken}
                fbWorker={fbWorker}
                tagOptions={tagOptions}
                toggleUploaderPanel={this.toggleUploaderPanel}
                updateUploaderStatus={this.updateUploaderStatus}
                openUploaderPanel={openUploaderPanel}
                displayError={this.displayError}
                updateAssetList={this.updateAssetList}
                onTagAdd={this.onTagAdd}
              />
              <AssetList
                mode={mode}
                user={user}
                tagOptions={tagOptions}
                updateTagOptions={this.updateTagOptions}
                onAssetClick={this.onAssetClick}
                uploadedAssets={uploadedAssets}
                selectedAssets={selectedAssets}
                updateSelectedAssets={this.updateSelectedAssets}
                updateAssetTags={this.updateAssetTags}
                updateMultipleTagOptions={this.updateMultipleTagOptions}
                updatedAssetTags={updatedAssetTags} // will be cleared once the assets are updated and re-rendered
                deletedAssets={deletedAssets}
                searchParams={searchParams}
                fbWorker={fbWorker}
                refreshAssetList={refreshAssetList}
                inArchiveMode={inArchiveMode}
                toggleArchivedStatusForAsset={this.toggleArchivedStatusForAsset}
              />

              {/* Error modal */}
              <Modal
                open={error && error.type !== null}
                size="small"
                onClose={this.onErrorModalClose}
              >
                <Modal.Header>
                  {error && error.type && error.title}
                </Modal.Header>
                <Modal.Content>
                  {error && error.type && error.message}
                </Modal.Content>
                <Modal.Actions>
                  <Button positive onClick={this.onErrorModalClose}>
                    Ok
                  </Button>
                </Modal.Actions>
              </Modal>
            </Container>
          </Modal.Content>
          <Modal.Actions>
            <Button
              onClick={this.onModalClose}
              disabled={mode !== 'NORMAL' && mode !== 'EDITTING'}
            >
              Close
            </Button>
            <Button
              disabled={
                mode === 'EDITTING' ||
                selectedAssets.length === 0 ||
                inArchiveMode
              }
              loading={selectedAssetDataLoading}
              onClick={this.onAssetSelectButtonClick}
            >
              Select
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
    );
  }
}

AssetGallery.defaultProps = {
  displayPreview: true // to decide if it needs to display selected asset in the selector
};

/* eslint react/forbid-prop-types: 0 */
AssetGallery.propTypes = {
  getFbToken: PropTypes.func.isRequired,
  displayPreview: PropTypes.bool
};

const mapStateToProps = (state) => ({
  ...state,
  user: state.user
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(AppActions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(AssetGallery);



// WEBPACK FOOTER //
// ./src/components/common/AssetGalleryV2/AssetGallery.jsx